import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';

import { AnalyticsProvider } from '../../providers';
import { useStyles } from './styles';

export default function OverlayCard({
  createdAt,
  description,
  hasMenu,
  image,
  key,
  link,
  onClick,
  onDelete,
  title,
  users,
}) {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const menuOpen = Boolean(anchorEl);
  const onOpenMenu = (event) => {
    AnalyticsProvider.event('OPEN_CARD_MENU');
    setAnchorEl(event.currentTarget);
  };
  const onCloseMenu = () => {
    setAnchorEl(null);
  };
  const onClickOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };
  const onCloseDeleteDialog = () => {
    setAnchorEl(null);
    setOpenDeleteDialog(false);
  };
  const onClickDelete = () => {
    AnalyticsProvider.event('CLICK_DELETE_OVERLAY');
    onDelete();
  };
  const onClickLink = () => {
    AnalyticsProvider.event('CLICK_CARD_LINK', {
      id: link?.id,
      template: key,
    });
    window.open(link?.url);
  };

  return (
    <Card className={classes.root} variant="outlined">
      <CardMedia
        alt="Overlay image"
        className={classes.media}
        image={image}
        onClick={onClick}
      />
      <CardContent>
        <Typography
          className={classes.title}
          gutterBottom
          variant="h5"
          component="div"
          onClick={onClick}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
        {!!users && (
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            mt={2}
          >
            <Box alignItems="center" display="flex">
              <PersonIcon className={classes.icon} />
              <Tooltip
                placement="top"
                title={i18n.t('components.card.usersUsingIt')}
              >
                <Typography variant="body2" color="text.secondary">
                  {users}
                </Typography>
              </Tooltip>
            </Box>
            {link && (
              <Box alignItems="center" display="flex" onClick={onClickLink}>
                <LinkIcon className={classes.linkIcon} />
              </Box>
            )}
          </Box>
        )}
        {!!createdAt && (
          <Box alignItems="center" display="flex" mt={2}>
            <CalendarTodayIcon className={classes.icon} />
            <Tooltip
              placement="top"
              title={i18n.t('components.card.createdAt')}
            >
              <Typography variant="body2" color="text.secondary">
                {createdAt}
              </Typography>
            </Tooltip>
          </Box>
        )}
        {hasMenu && (
          <>
            <IconButton
              aria-label="menu"
              className={classes.menu}
              onClick={onOpenMenu}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={onCloseMenu}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={onClickOpenDeleteDialog}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{i18n.t('common.delete')}</ListItemText>
              </MenuItem>
            </Menu>
            <Dialog
              open={openDeleteDialog}
              onClose={onCloseDeleteDialog}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {i18n.t('components.card.removePopup.title')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {i18n.t('components.card.removePopup.description')}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  autoFocus
                  onClick={onCloseDeleteDialog}
                  color="primary"
                  variant="contained"
                >
                  {i18n.t('common.cancel')}
                </Button>
                <Button onClick={onClickDelete} color="secondary">
                  {i18n.t('common.delete')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        {!createdAt && (
          <Button
            className={classes.button}
            color="success"
            fullWidth
            onClick={onClick}
            variant="contained"
          >
            {i18n.t('common.create')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

OverlayCard.propTypes = {
  createdAt: PropTypes.string,
  description: PropTypes.string,
  hasMenu: PropTypes.bool,
  image: PropTypes.string,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  title: PropTypes.string,
  users: PropTypes.number,
};

OverlayCard.defaultProps = {
  createdAt: '',
  description: '',
  hasMenu: false,
  image: '',
  onClick: undefined,
  onDelete: undefined,
  title: '',
  users: 0,
};
